import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  buildSelfEmployedFilter,
  selfEmployedListSearchSchema,
  selfEmploymentQueries,
  type SelfEmployedListSearch,
} from '@/entities/reports/self-employment';
import { SelfEmployedPage } from '@/pages/reports/self-employed';

/**
 * Self-employed couriers report — cpanel `/reports/self-employed`. The URL is
 * the single source of truth for the day window, search, status tab and page.
 * Prefetch is best-effort and non-blocking. Gated by the
 * `reports.self_employed.view` permission.
 */
export const Route = createFileRoute('/reports/self-employed')({
  staticData: { permission: 'reports.self_employed.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: selfEmployedListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient
      .prefetchQuery(selfEmploymentQueries.list(buildSelfEmployedFilter(deps).listFilter))
      .catch(() => undefined);
  },
  component: SelfEmployedRoute,
});

function SelfEmployedRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onParamsChange = (next: Partial<SelfEmployedListSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  return <SelfEmployedPage params={search} onParamsChange={onParamsChange} />;
}
